import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AppProvider, useApp } from './contexts/AppContext';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import SecurityGate from './components/SecurityGate';
import AiWakeIndicator from './components/AiWakeIndicator';
import AntiTamper from './components/AntiTamper';
import ErrorBoundary from './components/ErrorBoundary';
import CommandCenter from './pages/CommandCenter';
import NeoCommand from './pages/NeoCommand';
import NeoVision from './pages/NeoVision';
import NeoIntel from './pages/NeoIntel';
import DataCenter from './pages/DataCenter';
import SystemLog from './pages/SystemLog';
import Admin from './pages/Admin';
// SoulSearch diintegrasikan ke OsintTab (Feature 003 § 4.5) — bukan halaman terpisah
import { supabase } from './lib/supabase';
import { bootstrapHFFromSupabase } from './lib/storage';
import { pingHFSpace, startHFKeepAlive, syncMissingEmbeddings } from './lib/aiScanner';
import { initFaceEngine, syncAllEmbeddings as syncLightEmbeddings } from './lib/faceEngine';

const AppShell = () => {
  const { t } = useApp();
  // B-001: persist last page ke sessionStorage supaya browser refresh tidak reset ke Home.
  // S1-004: support URL param ?page=admin sebagai entry point Admin page
  // (tidak ada sidebar item — operator-only, gated SecurityGate level=admin).
  const [page, setPage] = useState(() => {
    try {
      const url = new URLSearchParams(window.location.search).get('page');
      if (url) return url;
    } catch { /* ignore */ }
    return sessionStorage.getItem('horus_last_page') || 'command-center';
  });
  useEffect(() => { sessionStorage.setItem('horus_last_page', page); }, [page]);
  const [alerts, setAlerts] = useState([]);
  const [kameras, setKameras] = useState([]);
  const [kendaraan, setKendaraan] = useState([]);
  const [wantedCount, setWantedCount] = useState(0);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [supaStatus, setSupaStatus] = useState('connecting'); // 'connecting'|'online'|'offline'
  const [hfStatus, setHfStatus]     = useState('waking');    // 'waking'|'online'|'offline'
  // engineStatus: engine recognition aktif saat ini.
  //  'hf'       = HF Space (ArcFace 512-dim) — engine utama
  //  'fallback' = faceEngine.js (FaceNet 128-dim) — browser-side, dipakai saat HF down
  //  'offline'  = tidak ada engine; hanya text search (NIK/nama) yang aktif
  const [engineStatus, setEngineStatus] = useState('hf');
  const toastTimerRef = useRef(null);
  const dismissedIds  = useRef(new Set());

  useEffect(() => {
    let mounted = true;
    // Heartbeat HF setiap 20 menit — supaya Space tidak sleep + auto-detect down
    const stopKeepAlive = startHFKeepAlive(async (next) => {
      if (!mounted) return;
      setHfStatus(next);
      if (next === 'online') {
        setEngineStatus('hf');
        // HF up → hydrate face_db + sync embedding 512-dim yang masih kosong
        bootstrapHFFromSupabase().then(r => {
          if (mounted && r && !r.error)
            console.log(`[bootstrapHF] loaded ${r.loaded ?? 0}, total: ${r.total_in_face_db ?? '?'}`);
        }).catch(() => {});
        syncMissingEmbeddings(supabase).catch(() => {});
      } else {
        // HF down → preload faceEngine 128-dim + sync embedding ringan client-side
        setEngineStatus('fallback');
        initFaceEngine().then(ok => {
          if (!mounted || !ok) {
            setEngineStatus('offline');
            return;
          }
          syncLightEmbeddings(supabase).catch(() => {});
        }).catch(() => setEngineStatus('offline'));
      }
    });
    // Initial wake-up — kasih HF kesempatan bangun lebih cepat (timeout 30s)
    pingHFSpace(30000).catch(() => {});
    Promise.all([
      supabase.from('alerts').select('*').order('created_at', { ascending: false }),
      supabase.from('kamera').select('*'),
      supabase.from('kendaraan').select('*').order('no_plat'),
      supabase.from('penduduk').select('*', { count: 'exact', head: true }).in('status_intelijen', ['WANTED','SUSPECT']),
    ]).then(([alertsRes, kameraRes, kendaraanRes, wantedRes]) => {
      if (!mounted) return;
      if (alertsRes.error)    console.warn('[boot] alerts:',    alertsRes.error.message);
      if (kameraRes.error)    console.warn('[boot] kamera:',    kameraRes.error.message);
      if (kendaraanRes.error) console.warn('[boot] kendaraan:', kendaraanRes.error.message);
      if (wantedRes.error)    console.warn('[boot] wanted:',    wantedRes.error.message);
      setAlerts(alertsRes.data ?? []);
      setKameras(kameraRes.data ?? []);
      setKendaraan(kendaraanRes.data ?? []);
      setWantedCount(wantedRes.count ?? 0);
    }).catch(e => console.error('[boot] load failed:', e));

    const channel = supabase
      .channel('app-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'alerts' }, ({ new: a }) => {
        setAlerts(prev => [a, ...prev].slice(0, 50));
        if (!dismissedIds.current.has(a.id)) {
          setToast(a);
          clearTimeout(toastTimerRef.current);
          toastTimerRef.current = setTimeout(() => setToast(null), 6000);
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'alerts' }, ({ new: a }) => {
        setAlerts(prev => prev.map(x => x.id === a.id ? a : x));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'kamera' }, ({ new: k }) => {
        setKameras(prev => [...prev, k]);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'kamera' }, ({ new: k }) => {
        setKameras(prev => prev.map(x => x.id === k.id ? k : x));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'kamera' }, ({ old: k }) => {
        setKameras(prev => prev.filter(x => x.id !== k.id));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'kendaraan' }, ({ new: k }) => {
        if (!k?.no_plat) return;
        setKendaraan(prev => prev.some(x => x.no_plat === k.no_plat)
          ? prev
          : [...prev, k].sort((a,b) => (a.no_plat || '').localeCompare(b.no_plat || '')));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'kendaraan' }, ({ new: k }) => {
        if (!k?.no_plat) return;
        setKendaraan(prev => prev.map(x => x.no_plat === k.no_plat ? k : x));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'kendaraan' }, ({ old: k }) => {
        // NOTE: requires REPLICA IDENTITY FULL on kendaraan if PK is not no_plat,
        // otherwise k.no_plat will be undefined and the row stays in UI.
        if (!k?.no_plat) {
          console.warn('[realtime] kendaraan DELETE without no_plat — set REPLICA IDENTITY FULL');
          return;
        }
        setKendaraan(prev => prev.filter(x => x.no_plat !== k.no_plat));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'penduduk' }, () => {
        supabase.from('penduduk').select('*', { count: 'exact', head: true })
          .in('status_intelijen', ['WANTED', 'SUSPECT'])
          .then(({ count }) => setWantedCount(count ?? 0));
      })
      .subscribe((status) => {
        if (!mounted) return;
        if (status === 'SUBSCRIBED') setSupaStatus('online');
        else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') setSupaStatus('offline');
      });

    return () => {
      mounted = false;
      stopKeepAlive?.();
      try { channel.unsubscribe(); } catch { /* noop */ }
      supabase.removeChannel(channel);
      clearTimeout(toastTimerRef.current);
    };
  }, []);


  const renderPage = () => {
    switch (page) {
      case 'neo-command':    return <NeoCommand />;
      case 'command-center': return <CommandCenter alerts={alerts} kameras={kameras} wantedCount={wantedCount} setPage={setPage} />;
      case 'neovision':      return <NeoVision kameras={kameras} setKameras={setKameras} hfStatus={hfStatus} engineStatus={engineStatus} setPage={setPage} />;
      case 'neointel':       return <SecurityGate pageName={t('gate_intel')} level="admin"><NeoIntel hfStatus={hfStatus} /></SecurityGate>;
      case 'datacenter':     return <SecurityGate pageName={t('gate_dc')} level="admin"><DataCenter kameras={kameras} setKameras={setKameras} kendaraan={kendaraan} setKendaraan={setKendaraan} setPage={setPage} /></SecurityGate>;
      case 'system-log':     return <SystemLog supaStatus={supaStatus} hfStatus={hfStatus} kameras={kameras} />;
      case 'admin':          return <SecurityGate pageName="ADMIN — Runtime Controls" level="admin"><Admin /></SecurityGate>;
      default:               return <CommandCenter alerts={alerts} kameras={kameras} wantedCount={wantedCount} setPage={setPage} />;
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden" style={{ background: 'var(--color-bg)', fontFamily: 'var(--font-sans)' }}>
      <Sidebar page={page} setPage={setPage} open={sidebarOpen} setOpen={setSidebarOpen} />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Header page={page} supaStatus={supaStatus} hfStatus={hfStatus} />
        <main className="flex-1 overflow-y-auto c-scroll" style={{ minHeight: 0 }}>
          {/* ErrorBoundary mencegah error di sub-page (mis. modal upload) crash
              seluruh tree → black screen. User tetap punya akses Sidebar+Header
              dan bisa Reload. */}
          <ErrorBoundary>
            {renderPage()}
          </ErrorBoundary>
        </main>
        <Footer />
      </div>

      {/* Indikator "AI sedang disiapkan" setelah login (#1057). */}
      <AiWakeIndicator />

      {toast && (
        <div
          className="fixed top-4 left-1/2 z-9999 animate-fadeInUp"
          style={{ transform: 'translateX(-50%)', minWidth: 340, maxWidth: 480 }}
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl"
            style={{
              background: toast.status_subjek === 'WANTED' || toast.status_subjek === 'SUSPECT'
                ? 'rgba(255,45,85,0.95)' : 'rgba(2,6,14,0.92)',
              border: `1px solid ${toast.status_subjek === 'WANTED' || toast.status_subjek === 'SUSPECT' ? '#ff2d55' : 'rgba(0,212,255,0.4)'}`,
              boxShadow: toast.status_subjek === 'WANTED' || toast.status_subjek === 'SUSPECT'
                ? '0 8px 32px rgba(255,45,85,0.35)' : '0 8px 32px rgba(0,212,255,0.2)',
              backdropFilter: 'blur(12px)',
            }}>
            <div className="w-2 h-2 rounded-full shrink-0 animate-blink"
              style={{ background: toast.status_subjek === 'WANTED' || toast.status_subjek === 'SUSPECT' ? '#fff' : '#ff2d55' }} />
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-black uppercase tracking-widest font-mono"
                style={{ color: toast.status_subjek === 'WANTED' || toast.status_subjek === 'SUSPECT' ? 'rgba(255,255,255,0.75)' : '#ff2d55' }}>
                {t('toast_new')} {toast.jenis?.replace(/_/g, ' ')}
              </p>
              <p className="text-sm font-bold truncate"
                style={{ color: '#fff' }}>{toast.subjek}</p>
              <p className="text-[9px] truncate"
                style={{ color: toast.status_subjek === 'WANTED' || toast.status_subjek === 'SUSPECT' ? 'rgba(255,255,255,0.7)' : 'var(--text-dim)' }}>
                {toast.lokasi} · {toast.waktu}
              </p>
            </div>
            <button
              onClick={() => { dismissedIds.current.add(toast.id); setToast(null); clearTimeout(toastTimerRef.current); }}
              className="shrink-0 flex items-center justify-center rounded-lg hover:opacity-70 transition-opacity"
              style={{ width: 24, height: 24, background: 'rgba(255,255,255,0.15)' }}>
              <span style={{ color: '#fff', fontSize: 14, lineHeight: 1 }}>×</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap entire App: AntiTamper (deterrent untuk DevTools/source view) → login global → AppShell.
// Login global level 'root' (keputusan Andra 17 Okt): tanpa token, AppShell tidak dipasang,
// jadi tidak ada fetch Supabase/HF sebelum login. SecurityGate per-halaman (level 'admin')
// di renderPage() tetap meminta login kedua untuk halaman sensitif.
const App = () => (
  <AppProvider>
    <AntiTamper>
      <div className="h-screen w-screen">
        <SecurityGate pageName="HORUS" level="root">
          <AppShell />
        </SecurityGate>
      </div>
    </AntiTamper>
  </AppProvider>
);

export default App;
